export type { AllocationPlanAdviceCardDisplay } from '../../types/adviceCards.types'
import type { ElitePrimeAllocationPlanData, ElitePrimeStrategyData } from '../../../../types/elitePrimeSummary.types'
import type { AdviceCardStat, AllocationPlanAdviceCardDisplay } from '../../types/adviceCards.types'
import {
  buildStrategyFindCopyDisplay,
  buildStrategyUnderReviewStats,
  isStrategyFindCopyStatus,
  isStrategyQuestionnaireSubmittedStatus
} from './adviceCardStrategyStatusDisplay'

function formatFundCountValue(count: number): string {
  return `${count} ${count === 1 ? 'fund' : 'funds'}`
}

function buildActivePlanStats(planData: ElitePrimeAllocationPlanData): AdviceCardStat[] {
  const stats: AdviceCardStat[] = []
  const fundsToSell = planData.fundsToSell ?? 0
  const fundsToBuy = planData.fundsToBuy ?? 0
  const fundsFrom = planData.fundsFrom ?? 0
  const fundsTo = planData.fundsTo ?? 0

  if (fundsToSell > 0) {
    stats.push({ value: formatFundCountValue(fundsToSell), label: 'to sell' })
  }

  if (fundsToBuy > 0) {
    stats.push({ value: formatFundCountValue(fundsToBuy), label: 'to buy' })
  }

  if (fundsFrom > 0 && fundsTo > 0 && fundsFrom !== fundsTo) {
    stats.push({
      value: `${formatFundCountValue(fundsFrom)} → ${formatFundCountValue(fundsTo)}`,
      label: fundsFrom > fundsTo ? 'to consolidate' : 'to diversify'
    })
  }

  return stats
}

function buildFundsUnderReviewStats(fundsUnderReview: number): AdviceCardStat[] {
  return [{ value: formatFundCountValue(fundsUnderReview), label: 'in review' }]
}

function buildEmptyAllocationStats(): AdviceCardStat[] {
  return [{ value: 'No funds', label: 'to reallocate' }]
}

export function buildAllocationPlanAdviceCardDisplay({
  planData,
  strategyData
}: {
  planData?: ElitePrimeAllocationPlanData
  strategyData?: ElitePrimeStrategyData
}): AllocationPlanAdviceCardDisplay {
  const status = strategyData?.status

  if (isStrategyFindCopyStatus(status)) {
    return buildStrategyFindCopyDisplay('Find your allocation strategy to get your allocation plan')
  }

  if (isStrategyQuestionnaireSubmittedStatus(status)) {
    return {
      stats: buildStrategyUnderReviewStats({
        planLabel: 'Allocation Plan',
        planAvailableAt: planData?.planAvailableAt,
        planAvailableInDays: strategyData?.planAvailableInDays
      }),
      insightTone: 'orange'
    }
  }

  if (!planData) {
    return { stats: [], insightTone: 'orange' }
  }

  const planAvailableAt = planData.planAvailableAt
  if ((planAvailableAt ?? 0) > Date.now()) {
    return {
      stats: buildStrategyUnderReviewStats({
        planLabel: 'Allocation Plan',
        planAvailableAt
      }),
      insightTone: 'orange'
    }
  }

  const activeStats = buildActivePlanStats(planData)
  if (activeStats.length > 0) {
    return { stats: activeStats, insightTone: 'orange' }
  }

  const fallbackStats: AdviceCardStat[] = []

  if (planData.fundsToSell === 0 && planData.fundsToBuy === 0) {
    fallbackStats.push(...buildEmptyAllocationStats())
  }

  const fundsUnderReview = planData.fundsUnderReview ?? 0
  if (fundsUnderReview > 0) {
    fallbackStats.push(...buildFundsUnderReviewStats(fundsUnderReview))
  }

  if (fallbackStats.length > 0) {
    return { stats: fallbackStats, insightTone: 'orange' }
  }

  return {
    stats: buildEmptyAllocationStats(),
    insightTone: 'orange'
  }
}
